/**
 * Security utilities for sanitizing user-provided data in exports.
 */

/**
 * Escapes characters for safe inclusion in HTML or SVG content.
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string.
 */
export function escapeHTML(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/[&<>"']/g, (m) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[m]));
}

/**
 * Sanitizes a string for use in CSS or JS comments to prevent breakout.
 * @param {string} str - The string to sanitize.
 * @returns {string} The sanitized string.
 */
export function sanitizeForComment(str) {
  if (typeof str !== 'string') return '';
  // Prevent comment breakout by removing */ and ensuring no newlines that could start new code
  return str.replace(/\*\//g, '* /').replace(/\n/g, ' ');
}

/**
 * Escapes single quotes and backslashes for safe inclusion in JS single-quoted strings.
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string.
 */
export function escapeJSString(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Sanitizes a color value to ensure it's a valid hex, rgb, rgba, hsl, or hsla string.
 * If invalid, returns a safe default.
 * @param {string} color - The color string to sanitize.
 * @returns {string} The sanitized color or a safe default.
 */
export function sanitizeColor(color) {
  if (typeof color !== 'string') return '#000000';

  // Basic validation for hex, rgb(a), hsl(a)
  // Hex: # followed by 3, 4, 6, or 8 hex digits
  const hexRegex = /^#([A-Fa-f0-9]{3,4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
  // rgb(a)/hsl(a): functional notation with numbers/percentages/commas/dots/slashes
  const funcRegex = /^(rgb|rgba|hsl|hsla)\([0-9%.,\s/()]+\)$/i;
  // Named colors (simplified - allow only alpha chars)
  const namedRegex = /^[a-z]+$/i;

  const trimmed = color.trim();
  if (hexRegex.test(trimmed) || funcRegex.test(trimmed) || namedRegex.test(trimmed)) {
    return trimmed;
  }

  // If it doesn't match, try to see if it's a hex without #
  if (/^[A-Fa-f0-9]{3,8}$/.test(trimmed)) {
    return '#' + trimmed;
  }

  return '#000000'; // Default safe color
}

/**
 * Sanitizes a string for use as a JS identifier or CSS variable name.
 * @param {string} str - The string to sanitize.
 * @returns {string} The sanitized string.
 */
export function sanitizeForIdentifier(str) {
  if (typeof str !== 'string') return '';
  // Allow only alphanumeric characters, underscores, and hyphens
  // Replace anything else with a hyphen
  let sanitized = str.replace(/[^a-z0-9-_]/gi, '-');

  // Ensure it doesn't start with a digit if it's meant for JS,
  // but for CSS variables/general identifiers, we'll just ensure it's not empty
  if (/^[0-9]/.test(sanitized)) {
    sanitized = '_' + sanitized;
  }

  return sanitized || 'unnamed';
}
